/**
 * Die Endpunkte, die dieser Client benutzt.
 *
 * **Eine Funktion je Route**, und jede nennt ihr Schema aus `@flaghex/contracts`. Damit steht
 * an genau einer Stelle, welche Antwortform erwartet wird — eine Komponente, die selbst einen
 * Pfad zusammensetzt, wäre die zweite.
 *
 * **Die Liste wächst mit den Zyklen** und nicht vorab. Ein Endpunkt, den noch niemand aufruft,
 * ist unbenutzter Code, dessen Form nichts bestätigt; die Routentabelle steht vollständig in
 * `apps/server/README.md` und in der OpenAPI-Spezifikation.
 */

import {
  ADMIN_KEY_HEADER,
  addBotResponseSchema,
  llmModelsSchema,
  HOST_TOKEN_HEADER,
  INVITE_TOKEN_HEADER,
  SESSION_TOKEN_HEADER,
  createGameResponseSchema,
  gameDefaultsSchema,
  healthSchema,
  hostTokenCreatedSchema,
  hostTokenListSchema,
  publisherKeyCreatedSchema,
  publisherKeyEventListSchema,
  publisherKeyListSchema,
  noContentSchema,
  inviteTokenSchema,
  joinResponseSchema,
  lobbyViewSchema,
  ruleConfigResponseSchema,
  type AddBotResponse,
  type BotChoice,
  type CreateGameRequest,
  type CreateGameResponse,
  type GameConfigurationDto,
  type GameDefaults,
  type Health,
  type HostTokenCreatedDto,
  type HostTokenDto,
  type PublisherKeyCreatedDto,
  type PublisherKeyDto,
  type PublisherKeyEventDto,
  type InviteTokenDto,
  type JoinRequest,
  type JoinResponse,
  type LlmModels,
  type LobbyView,
  type SectorIdDto,
} from '@flaghex/contracts';
import type { RuleConfig } from '@flaghex/engine';

import type { ApiResult, Transport } from './transport.js';

/** Antwortet der Dienst? Öffentlich, ohne Berechtigung. */
export function health(request: Transport): Promise<ApiResult<Health>> {
  return request({ method: 'GET', path: '/api/v1/health', schema: healthSchema });
}

/**
 * Die Berechtigung des Admins — **immer als Header**.
 *
 * Der Name kommt aus `@flaghex/contracts` und nicht aus einer Zeichenkette hier: er ist ein
 * Vertrag zwischen Browser und Dienst, und zwei Stellen, die ihn schreiben, laufen auseinander.
 * Der Fehler sähe dann nach einem falschen Schlüssel aus.
 */
function adminAuth(adminKey: string): { readonly header: string; readonly value: string } {
  return { header: ADMIN_KEY_HEADER, value: adminKey };
}

/** Die globale Regelkonfiguration (§52). */
export function ruleConfig(request: Transport, adminKey: string): Promise<ApiResult<RuleConfig>> {
  return request({
    method: 'GET',
    path: '/api/v1/admin/rule-config',
    schema: ruleConfigResponseSchema,
    token: adminAuth(adminKey),
  });
}

/**
 * Die globale Regelkonfiguration ersetzen.
 *
 * **Vollständig, immer.** Die Route ersetzt und ergänzt nicht: weggelassene Felder fallen auf
 * ihren Default zurück (`.prefault()` im Schema). Wer nur die Wächter-Reichweite ändern will und
 * ein Teilobjekt schickt, setzt damit nebenbei das Punkteziel und alle Figurenzahlen zurück —
 * deshalb nimmt diese Funktion eine ganze `RuleConfig` und kein Teilstück.
 *
 * Wirkt nur auf **neue** Partien; bestehende behalten ihren Snapshot (§53).
 */
export function saveRuleConfig(
  request: Transport,
  adminKey: string,
  config: RuleConfig,
): Promise<ApiResult<RuleConfig>> {
  return request({
    method: 'PUT',
    path: '/api/v1/admin/rule-config',
    schema: ruleConfigResponseSchema,
    token: adminAuth(adminKey),
    body: config,
  });
}

/** Alle Host-Links — ohne Token und ohne Hash, die gibt der Dienst nicht heraus. */
export function hostTokens(
  request: Transport,
  adminKey: string,
): Promise<ApiResult<readonly HostTokenDto[]>> {
  return request({
    method: 'GET',
    path: '/api/v1/admin/host-tokens',
    schema: hostTokenListSchema,
    token: adminAuth(adminKey),
  });
}

/**
 * Einen Host-Link anlegen.
 *
 * **Die einzige Antwort, die den Klartext enthält.** Danach existiert nur sein Hash; ein verlorener
 * Link wird ersetzt, nicht wiederhergestellt. Die Oberfläche muss ihn deshalb sofort zeigen.
 */
export function createHostToken(
  request: Transport,
  adminKey: string,
  label: string,
): Promise<ApiResult<HostTokenCreatedDto>> {
  return request({
    method: 'POST',
    path: '/api/v1/admin/host-tokens',
    schema: hostTokenCreatedSchema,
    token: adminAuth(adminKey),
    body: { label },
  });
}

/**
 * Einen Host-Link widerrufen.
 *
 * Widerrufen ist **nicht** löschen: die Zeile bleibt, damit nachvollziehbar bleibt, über welchen
 * Link eine Partie entstand. Idempotent, Antwort 204 — also ein leerer Rumpf, den der Transport
 * als `null` liest.
 */
export function revokeHostToken(
  request: Transport,
  adminKey: string,
  id: string,
): Promise<ApiResult<null>> {
  return request({
    method: 'DELETE',
    path: `/api/v1/admin/host-tokens/${id}`,
    schema: noContentSchema,
    token: adminAuth(adminKey),
  });
}

// ─── Die Verlagsschlüssel und die Anleitung (`W2`, E135) ──────────────────────────────────

/** Alle Verlagsschlüssel — ohne Schlüssel und ohne Hash. */
export function publisherKeys(
  request: Transport,
  adminKey: string,
): Promise<ApiResult<readonly PublisherKeyDto[]>> {
  return request({
    method: 'GET',
    path: '/api/v1/admin/publisher-keys',
    schema: publisherKeyListSchema,
    token: adminAuth(adminKey),
  });
}

/** Einen Verlagsschlüssel anlegen — die einzige Antwort, die ihn im Klartext enthält. */
export function createPublisherKey(
  request: Transport,
  adminKey: string,
  label: string,
): Promise<ApiResult<PublisherKeyCreatedDto>> {
  return request({
    method: 'POST',
    path: '/api/v1/admin/publisher-keys',
    schema: publisherKeyCreatedSchema,
    token: adminAuth(adminKey),
    body: { label },
  });
}

/** Der Verlauf eines Verlagsschlüssels (`W3`, **E136**), das Neueste zuerst. */
export function publisherKeyEvents(
  request: Transport,
  adminKey: string,
  id: string,
): Promise<ApiResult<readonly PublisherKeyEventDto[]>> {
  return request({
    method: 'GET',
    path: `/api/v1/admin/publisher-keys/${id}/events`,
    schema: publisherKeyEventListSchema,
    token: adminAuth(adminKey),
  });
}

/** Einen Verlagsschlüssel widerrufen. Sperrt sofort, die Zeile bleibt. */
export function revokePublisherKey(request: Transport, adminKey: string, id: string): Promise<ApiResult<null>> {
  return request({
    method: 'DELETE',
    path: `/api/v1/admin/publisher-keys/${id}`,
    schema: noContentSchema,
    token: adminAuth(adminKey),
  });
}

/**
 * Die Anleitung freischalten. Der Schlüssel reist **im Körper**, nicht in der Adresse; bei Erfolg
 * setzt der Dienst ein Cookie für `/anleitung`, das kein Skript lesen kann (`HttpOnly`).
 */
export function unlockManual(request: Transport, key: string): Promise<ApiResult<null>> {
  return request({ method: 'POST', path: '/api/v1/manual/unlock', schema: noContentSchema, body: { key } });
}

// ─── Die Partie: erstellen, bedienen, beitreten (Zyklus 10e) ──────────────────────────────
//
// Drei Berechtigungen, drei Header — und **keine** davon steht je in einem Pfad. Das wiegt hier
// schwerer als bei den Admin-Routen, denn Host- und Einladungslink werden absichtlich
// weitergegeben: im Pfad stünden sie im Zugriffslog von nginx und im `Referer` jedes Mitspielers
// (`ARCHITECTURE.md` E26). Die **Partie-ID** darf dagegen in der Adresse stehen — sie ist kein
// Geheimnis, sondern eine Adresse, und der Server prüft den Besitz (E23).

function hostAuth(hostToken: string): { readonly header: string; readonly value: string } {
  return { header: HOST_TOKEN_HEADER, value: hostToken };
}

function inviteAuth(inviteToken: string): { readonly header: string; readonly value: string } {
  return { header: INVITE_TOKEN_HEADER, value: inviteToken };
}

/**
 * Eine Partie erstellen (§42, Nr. 27).
 *
 * Der Host ist **Spieler Nummer eins** — Anzeigename und Farbe gehen mit. Die Antwort trägt beide
 * Geheimnisse, und zwar **genau einmal**: den Einladungslink für die Mitspieler und den
 * Sitzungs-Token für den eigenen Platz. Danach existiert nur noch ihr Hash.
 */
export function createGame(
  request: Transport,
  hostToken: string,
  body: CreateGameRequest,
): Promise<ApiResult<CreateGameResponse>> {
  return request({
    method: 'POST',
    path: '/api/v1/games',
    schema: createGameResponseSchema,
    token: hostAuth(hostToken),
    body,
  });
}

/**
 * Die Partie beenden, für alle (`R12`) — nur der Host. In der Lobby schließt es sie, danach
 * beendet es sie über ihre Sitzung. Der Host-Link ist danach frei.
 */
export function abortGame(
  request: Transport,
  hostToken: string,
  gameId: string,
): Promise<ApiResult<null>> {
  return request({
    method: 'POST',
    path: `/api/v1/games/${gameId}/abort`,
    schema: noContentSchema,
    token: hostAuth(hostToken),
  });
}

/**
 * Die Vorgaben für das Formular des Hosts (`R10`): Punkteziel und Score-Reset aus der globalen
 * Konfiguration, damit das Formular sie vorbelegt zeigt.
 */
export function gameDefaults(
  request: Transport,
  hostToken: string,
): Promise<ApiResult<GameDefaults>> {
  return request({
    method: 'GET',
    path: '/api/v1/games/defaults',
    schema: gameDefaultsSchema,
    token: hostAuth(hostToken),
  });
}

/**
 * Die laufende Partie **dieses Host-Links**, falls es eine gibt.
 *
 * Beantwortet die Frage, die eine Sitzung nicht beantwortet: eine Sitzung gehört einem
 * **Spieler**, nicht einem Link. Ohne diese Abfrage zeigte ein zweiter Host-Link die Lobby der
 * Partie, in der der Betrachter zufällig mitspielt — gefunden bei der Sichtprüfung im Browser
 * (Zyklus 10e). `NOT_FOUND` heißt: dieser Link ist frei und darf anlegen.
 */
export function currentGame(
  request: Transport,
  hostToken: string,
): Promise<ApiResult<LobbyView>> {
  return request({
    method: 'GET',
    path: '/api/v1/games/current',
    schema: lobbyViewSchema,
    token: hostAuth(hostToken),
  });
}

/**
 * Die Konfiguration ersetzen (Nr. 27) — **vollständig**, Einstellungen und Farbplätze.
 *
 * Dieselbe Eigenschaft wie bei der RuleConfig: eine Teilangabe wäre kein Sparen, sondern ein
 * stiller Verlust. Ein entfallender Farbplatz macht seinen Spieler zum Wartenden (Nr. 30).
 */
export function configureGame(
  request: Transport,
  hostToken: string,
  gameId: string,
  configuration: GameConfigurationDto,
): Promise<ApiResult<null>> {
  return request({
    method: 'PUT',
    path: `/api/v1/games/${gameId}/configuration`,
    schema: noContentSchema,
    token: hostAuth(hostToken),
    body: configuration,
  });
}

/**
 * Einen Spieler einer Farbe zuordnen — oder die Zuordnung lösen (Nr. 29).
 *
 * `null` ist **ausdrücklich** und nicht weggelassen: ein fehlendes Feld wäre zweideutig („nicht
 * ändern" oder „lösen"?), und Raten ist hier falsch.
 */
export function assignSector(
  request: Transport,
  hostToken: string,
  gameId: string,
  playerId: string,
  sector: SectorIdDto | null,
): Promise<ApiResult<null>> {
  return request({
    method: 'POST',
    path: `/api/v1/games/${gameId}/assignments`,
    schema: noContentSchema,
    token: hostAuth(hostToken),
    body: { playerId, sector },
  });
}

/**
 * Einen Bot auf einen freien Farbplatz setzen (`V2-2f`, **E64**, **E65**).
 *
 * **Der Farbplatz ist Pflicht**, anders als beim Beitritt eines Menschen (Nr. 29): einen Bot
 * setzt der Host, und er weiß wohin. **Den Namen tippt er** — nur so sind zwei Konfigurationen
 * derselben Strategie unterscheidbar.
 *
 * **Seit `L1c2` schickt dieser Aufruf eine Wahl, keine Definition** (**E104**), seit `L2` die des
 * Hosts aus dem Formular (**E110**). Ausführungsart und Budget setzt der Dienst (**E105**,
 * **E106**).
 *
 * **Die Antwort trägt keinen Sitzungs-Token** (**E60**): ein Bot hat nichts nachzuweisen, und
 * `addBotResponseSchema` ist ein `strictObject` ohne das Feld.
 */
export function addBot(
  request: Transport,
  hostToken: string,
  gameId: string,
  bot: {
    readonly displayName: string;
    readonly sector: SectorIdDto;
    /** Die Wahl des Hosts (`L2`) — eine Stufe, eine Strategie mit Tiefe und Gewichten, ein Modell. */
    readonly choice: BotChoice;
  },
): Promise<ApiResult<AddBotResponse>> {
  return request({
    method: 'POST',
    path: `/api/v1/games/${gameId}/bots`,
    schema: addBotResponseSchema,
    token: hostAuth(hostToken),
    body: { displayName: bot.displayName, sector: bot.sector, choice: bot.choice },
  });
}

/**
 * Die Sprachmodelle, die der Dienst gerade fragen kann (`L2`, **E109**) — **öffentlich**, ohne
 * Token. Nur Kennungen und die Deckel; eine leere Liste heißt: kein Sprachmodell wählbar.
 */
export function llmModels(request: Transport): Promise<ApiResult<LlmModels>> {
  return request({ method: 'GET', path: '/api/v1/llm/models', schema: llmModelsSchema });
}

/** Einen Spieler aus der Lobby entfernen (Nr. 30). */
export function removePlayer(
  request: Transport,
  hostToken: string,
  gameId: string,
  playerId: string,
): Promise<ApiResult<null>> {
  return request({
    method: 'DELETE',
    path: `/api/v1/games/${gameId}/players/${playerId}`,
    schema: noContentSchema,
    token: hostAuth(hostToken),
  });
}

/**
 * Die Partie starten — LOBBY → SETUP (§47).
 *
 * Geht erst, wenn jeder Farbplatz belegt ist (Nr. 28) und mindestens zwei Seiten übrigbleiben
 * (Nr. 24). `canStart` in der Lobby sagt dasselbe vorher — **gerechnet wird es serverseitig**
 * (Regel 1), der Client zeigt es nur an.
 */
export function startGame(
  request: Transport,
  hostToken: string,
  gameId: string,
): Promise<ApiResult<null>> {
  return request({
    method: 'POST',
    path: `/api/v1/games/${gameId}/start`,
    schema: noContentSchema,
    token: hostAuth(hostToken),
  });
}

/**
 * Einen neuen Einladungslink erzeugen; der alte verfällt.
 *
 * Nötig, weil der Klartext genau einmal existiert — ein verlorener Link ist nicht
 * wiederherstellbar, sondern nur ersetzbar.
 */
export function renewInviteToken(
  request: Transport,
  hostToken: string,
  gameId: string,
): Promise<ApiResult<InviteTokenDto>> {
  return request({
    method: 'POST',
    path: `/api/v1/games/${gameId}/invite-token`,
    schema: inviteTokenSchema,
    token: hostAuth(hostToken),
  });
}

/**
 * Die Lobby mit dem Einladungslink — **vor** dem Beitritt.
 *
 * Der Endpunkt nennt die Partie absichtlich nicht: die Partie bestimmt der Token, also kann es
 * keine Abweichung zwischen genannter und berechtigter Partie geben.
 */
export function lobbyByInvite(
  request: Transport,
  inviteToken: string,
): Promise<ApiResult<LobbyView>> {
  return request({
    method: 'GET',
    path: '/api/v1/games/lobby',
    schema: lobbyViewSchema,
    token: inviteAuth(inviteToken),
  });
}

/** Dieselbe Lobby mit der eigenen Sitzung — nach dem Beitritt und nach einem Neuladen. */
export function lobbyBySession(
  request: Transport,
  sessionToken: string,
): Promise<ApiResult<LobbyView>> {
  return request({
    method: 'GET',
    path: '/api/v1/games/lobby',
    schema: lobbyViewSchema,
    token: { header: SESSION_TOKEN_HEADER, value: sessionToken },
  });
}

/**
 * Über den Einladungslink beitreten (§44).
 *
 * Die Farbwahl ist **optional** (Nr. 29): wer keine wählt, wartet in der Lobby, bis er wählt oder
 * der Host ihm eine zuordnet. Die Antwort trägt den Sitzungs-Token — das einzige Mal.
 */
export function joinGame(
  request: Transport,
  inviteToken: string,
  body: JoinRequest,
): Promise<ApiResult<JoinResponse>> {
  return request({
    method: 'POST',
    path: '/api/v1/games/join',
    schema: joinResponseSchema,
    token: inviteAuth(inviteToken),
    body,
  });
}
